/**
 * ReportDeathPage — death reporting form for primary executors.
 *
 * Submits to POST /api/clients/:clientId/report-death.
 * Shows death certificate upload when verificationMethod === "death_certificate".
 * Redirects to /waiting (grace_period) or /dashboard (verified) based on returned event status.
 */

import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ArrowLeft, Upload, FileCheck, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ReportDeathPageProps {
  clientId: string;
  clientName?: string; // optional — will be fetched from contexts if not provided
}

interface CertFile {
  filename: string;
  contentType: string;
  data: string; // base64
  displayName: string;
  sizeKb: number;
}

export default function ReportDeathPage({ clientId, clientName: clientNameProp }: ReportDeathPageProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [dateOfDeath, setDateOfDeath] = useState("");
  const [placeOfDeath, setPlaceOfDeath] = useState(""); // Phase 6c rev-2 #8 — feeds the 74A/74.1A cell
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [certFile, setCertFile] = useState<CertFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resolve planner name from contexts if not passed directly
  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
    enabled: !clientNameProp,
  });
  const clientName =
    clientNameProp ??
    contexts?.executorFor?.find((c: any) => c.clientId === clientId)?.plannerName ??
    "the plan owner";

  // Fetch death verification settings to show cert upload UI if needed
  const { data: deathSettings } = useQuery<{ deathVerificationMethod: string; gracePeriodHours: number }>({
    queryKey: ["/api/clients", clientId, "death-verification-settings"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/death-verification-settings`);
      if (!res.ok) throw new Error("Failed to load settings");
      return res.json();
    },
    enabled: !!clientId,
  });

  const requiresCert = deathSettings?.deathVerificationMethod === "death_certificate";

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_SIZE) {
      toast({ title: "File too large", description: "Maximum file size is 10 MB.", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the "data:mime/type;base64," prefix
      const base64 = dataUrl.split(",")[1];
      setCertFile({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        data: base64,
        displayName: file.name,
        sizeKb: Math.round(file.size / 1024),
      });
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be re-selected if removed
    e.target.value = "";
  };

  const reportMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, any> = {
        dateOfDeath: dateOfDeath || undefined,
        placeOfDeath: placeOfDeath || undefined,
        notes: notes || undefined,
      };
      if (certFile) {
        payload.certificateFile = {
          filename: certFile.filename,
          contentType: certFile.contentType,
          data: certFile.data,
        };
      }
      const res = await apiRequest("POST", `/api/clients/${clientId}/report-death`, payload);
      return res.json();
    },
    onSuccess: (event: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "death-event"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/contexts"] });
      toast({ title: "Death reported", description: "The estate plan verification process has been started." });
      if (event?.status === "pending_grace_period") {
        setLocation(`/executor/${clientId}/waiting`);
      } else {
        setLocation(`/executor/${clientId}/dashboard`);
      }
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to report death.", variant: "destructive" });
    },
  });

  return (
    <div className="p-6 max-w-lg mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button aria-label="Back" variant="ghost" size="icon" onClick={() => setLocation(`/executor/${clientId}/progress`)} data-testid="button-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Report a Death</h1>
          <p className="text-sm text-muted-foreground">{clientName}'s estate plan</p>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">This action starts the estate access process.</p>
          <p className="mt-1">
            Depending on the verification method chosen by {clientName}, executor access may be granted
            immediately or after a waiting period. {/* D-229.102 C1: both stops exist now (D-229.48, D-229.67). */}
            During a waiting period, {(clientName ?? "").trim().split(/\s+/)[0] || "the plan owner"} is
            alerted straight away and can stop the release in one click, and you can withdraw your report.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Death Details</CardTitle>
          <CardDescription>Provide what information you have. These fields are optional.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="date-of-death">Date of Passing</Label>
            <Input
              id="date-of-death"
              type="date"
              value={dateOfDeath}
              onChange={(e) => setDateOfDeath(e.target.value)}
              data-testid="input-date-of-death"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="place-of-death">Place of Passing</Label>
            <Input
              id="place-of-death"
              type="text"
              placeholder="City / town where the death occurred"
              value={placeOfDeath}
              onChange={(e) => setPlaceOfDeath(e.target.value)}
              data-testid="input-place-of-death"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any relevant information about the circumstances…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="input-death-notes"
            />
          </div>
        </CardContent>
      </Card>

      {requiresCert && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Death Certificate</CardTitle>
            <CardDescription>
              {clientName}'s verification method requires a death certificate. Upload a scan or photo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="input-certificate-file"
            />
            {certFile ? (
              <div className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <FileCheck className="w-4 h-4 text-green-600 shrink-0" />
                  <span className="font-medium truncate max-w-xs">{certFile.displayName}</span>
                  <span className="text-green-600 shrink-0">{certFile.sizeKb} KB</span>
                </div>
                <button
                  onClick={() => setCertFile(null)}
                  className="ml-2 p-1 rounded hover:bg-green-100 transition-colors"
                  title="Remove file"
                  data-testid="button-remove-cert"
                >
                  <X className="w-4 h-4 text-green-700" />
                </button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                data-testid="button-upload-cert"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Death Certificate
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => setLocation(`/executor/${clientId}/progress`)} data-testid="button-cancel-report">
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={() => setConfirmOpen(true)}
          disabled={requiresCert && !certFile}
          data-testid="button-submit-report"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Submit Death Report
        </Button>
      </div>

      {requiresCert && !certFile && (
        <p className="text-xs text-muted-foreground text-right">
          A death certificate is required before submitting.
        </p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Death Report</AlertDialogTitle>
            <AlertDialogDescription>
              You are reporting that <strong>{clientName}</strong> has passed away. This will begin
              the executor access verification process. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                reportMutation.mutate();
              }}
              disabled={reportMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {reportMutation.isPending ? "Submitting…" : "Yes, Submit Report"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
